// CreatorProfile — Phase 11 Step 4 Block 3.
//
// Gliederung nach HANDOFF-PROMPT-PHASE-11-STEP-4.md:
//   1. Header (Avatar 96px, Name+Handle, Bio, Stats-Row, Follow-Button)
//   2. Tabs (Scrollys default, About)
//   3. Scrollys-Tab (CardGrid, sortiert nach publishedAt desc)
//   4. About-Tab (längere Bio + Website + Social)
//
// Follow-Button ist optimistic-stub — `useState<boolean>` + alert-Fallback
// mit TODO für späteres Toast-System. Kein Backend.

import { useMemo, useState } from "react";
import { CardGrid } from "../feed/CardGrid";
import { ScrollyCard } from "../feed/ScrollyCard";
import { IconCheckBadge } from "../common/Icons";
import { FONT_DISPLAY, PINK, VARIANT } from "../../lib/tokens";
import { useVariant } from "../../hooks/useVariant";
import { formatViews } from "../../lib/format";
import {
  scrollys as ALL_SCROLLYS,
  type MockScrolly,
} from "../../lib/mock/scrollys";
import type { MockCreator } from "../../lib/mock/creators";

type Props = {
  creator: MockCreator;
};

type TabId = "scrollys" | "about";

export function CreatorProfile({ creator }: Props) {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  const [activeTab, setActiveTab] = useState<TabId>("scrollys");

  const scrollys = useMemo(
    () => filterAndSortScrollysForCreator(ALL_SCROLLYS, creator.handle),
    [creator.handle],
  );
  const totalViews = useMemo(
    () => scrollys.reduce((sum, s) => sum + s.stats.views, 0),
    [scrollys],
  );

  return (
    <div
      style={{
        padding: "40px 32px 80px",
        maxWidth: 1600,
        margin: "0 auto",
      }}
    >
      <CreatorHeader
        creator={creator}
        scrollyCount={scrollys.length}
        totalViews={totalViews}
      />

      <TabBar
        activeTab={activeTab}
        onChange={setActiveTab}
        scrollyCount={scrollys.length}
      />

      {activeTab === "scrollys" ? (
        <ScrollysTab scrollys={scrollys} />
      ) : (
        <AboutTab creator={creator} muted={v.muted} text={v.text} />
      )}
    </div>
  );
}

// ---- Creator-Filter ----------------------------------------------------

/**
 * Filtert Scrollys auf einen Creator und sortiert sie "publishedAt desc".
 * Unsere Mocks haben `publishedAt` als Freitext (z.B. "vor 3 Tagen"),
 * nicht als Datum — deshalb nutzen wir die Reihenfolge im Mock-Array
 * als Proxy für "neuer zuerst" (das entspricht der Dev-Konvention in
 * scrollys.ts: neueste Einträge stehen oben).
 */
function filterAndSortScrollysForCreator(
  all: MockScrolly[],
  handle: string,
): MockScrolly[] {
  return all.filter((s) => s.creator.handle === handle);
}

// ---- Header ------------------------------------------------------------

function CreatorHeader({
  creator,
  scrollyCount,
  totalViews,
}: {
  creator: MockCreator;
  scrollyCount: number;
  totalViews: number;
}) {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  const [following, setFollowing] = useState(false);

  const onFollowClick = () => {
    setFollowing((prev) => {
      const next = !prev;
      // TODO(phase-11+): echtes Toast-System statt alert.
      if (typeof window !== "undefined") {
        const label = next
          ? `Du folgst jetzt ${creator.name}.`
          : `Du folgst ${creator.name} nicht mehr.`;
        window.alert(label);
      }
      return next;
    });
  };

  return (
    <header
      style={{
        display: "flex",
        gap: 28,
        alignItems: "flex-start",
        paddingBottom: 28,
        borderBottom: `1px solid ${v.border}`,
      }}
    >
      <Avatar name={creator.name} color={creator.avatar} size={96} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <h1
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 500,
              fontSize: 44,
              margin: 0,
              letterSpacing: -0.8,
              lineHeight: 1.05,
              color: v.text,
            }}
          >
            {creator.name}
          </h1>
          {creator.verified && <IconCheckBadge size={18} />}
        </div>

        <div
          style={{
            fontSize: 14,
            color: v.muted,
            marginTop: 4,
          }}
        >
          @{creator.handle}
        </div>

        {creator.bio && (
          <p
            style={{
              margin: "14px 0 0",
              fontSize: 15,
              color: v.text,
              maxWidth: 560,
              lineHeight: 1.5,
              // 2-Zeilen-Clamp für Header-Bio (Briefing).
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {creator.bio}
          </p>
        )}

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 18,
            color: v.muted,
            fontSize: 13,
            flexWrap: "wrap",
          }}
        >
          <Stat>
            <strong style={{ color: v.text }}>{scrollyCount}</strong>{" "}
            {scrollyCount === 1 ? "Scrolly" : "Scrollys"}
          </Stat>
          <Stat>
            <strong style={{ color: v.text }}>{creator.followers}</strong>{" "}
            Follower
          </Stat>
          <Stat>
            <strong style={{ color: v.text }}>{formatViews(totalViews)}</strong>{" "}
            Views
          </Stat>
        </div>
      </div>

      <FollowButton following={following} onClick={onFollowClick} />
    </header>
  );
}

function Stat({ children }: { children: React.ReactNode }) {
  return <span>{children}</span>;
}

function FollowButton({
  following,
  onClick,
}: {
  following: boolean;
  onClick: () => void;
}) {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  const filledStyle = {
    background: PINK,
    color: "#fff",
    border: "1px solid transparent",
  };
  const outlineStyle = {
    background: "transparent",
    color: v.text,
    border: `1px solid ${v.border}`,
  };
  const style = following ? outlineStyle : filledStyle;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={following}
      style={{
        ...style,
        padding: "10px 22px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {following ? "Folgst du" : "Folgen"}
    </button>
  );
}

// ---- Avatar ------------------------------------------------------------
// Eigenständige Avatar-Komponente — analog ScrollyCard/FeaturedHero, aber
// mit variabler Größe (Header: 96px).

function Avatar({
  name,
  color,
  size,
}: {
  name: string;
  color: string;
  size: number;
}) {
  const initial =
    name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color || PINK,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 600,
        fontSize: size * 0.38,
        flexShrink: 0,
        letterSpacing: -1,
        fontFamily: "inherit",
      }}
      aria-hidden="true"
    >
      {initial}
    </div>
  );
}

// ---- Tabs --------------------------------------------------------------

function TabBar({
  activeTab,
  onChange,
  scrollyCount,
}: {
  activeTab: TabId;
  onChange: (t: TabId) => void;
  scrollyCount: number;
}) {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  return (
    <div
      role="tablist"
      aria-label="Creator-Bereiche"
      style={{
        display: "flex",
        gap: 6,
        borderBottom: `1px solid ${v.border}`,
        marginTop: 24,
        marginBottom: 28,
      }}
    >
      <TabButton
        id="scrollys"
        label="Scrollys"
        badge={scrollyCount}
        activeTab={activeTab}
        onChange={onChange}
      />
      <TabButton
        id="about"
        label="About"
        activeTab={activeTab}
        onChange={onChange}
      />
    </div>
  );
}

function TabButton({
  id,
  label,
  badge,
  activeTab,
  onChange,
}: {
  id: TabId;
  label: string;
  badge?: number;
  activeTab: TabId;
  onChange: (t: TabId) => void;
}) {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  const active = activeTab === id;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onChange(id)}
      style={{
        border: "none",
        background: "transparent",
        color: active ? v.text : v.muted,
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        padding: "10px 14px",
        cursor: "pointer",
        fontFamily: "inherit",
        borderBottom: `2px solid ${active ? PINK : "transparent"}`,
        marginBottom: -1,
      }}
    >
      {label}
      {typeof badge === "number" && (
        <span
          style={{
            marginLeft: 6,
            fontSize: 12,
            color: v.muted,
            fontWeight: 500,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

// ---- Scrollys-Tab ------------------------------------------------------

function ScrollysTab({ scrollys }: { scrollys: MockScrolly[] }) {
  const { variant } = useVariant();
  const v = VARIANT[variant];
  if (scrollys.length === 0) {
    return (
      <div
        style={{
          padding: "48px 16px",
          textAlign: "center",
          border: `1px dashed ${v.border}`,
          borderRadius: 14,
          color: v.muted,
          maxWidth: 520,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 22,
            color: v.text,
            marginBottom: 4,
          }}
        >
          Noch nichts veröffentlicht
        </div>
        <p style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>
          Sobald dieser Creator Scrollys veröffentlicht, erscheinen sie hier.
        </p>
      </div>
    );
  }
  return (
    <CardGrid>
      {scrollys.map((s) => (
        <ScrollyCard key={s.id} scrolly={s} />
      ))}
    </CardGrid>
  );
}

// ---- About-Tab ---------------------------------------------------------

function AboutTab({
  creator,
  muted,
  text,
}: {
  creator: MockCreator;
  muted: string;
  text: string;
}) {
  const bio = creator.bioLong ?? creator.bio;
  const hasWebsite = !!creator.website;
  const social = creator.social ?? {};
  const hasSocial = Object.values(social).some(Boolean);

  return (
    <article
      style={{
        maxWidth: 720,
        color: text,
        fontSize: 15,
        lineHeight: 1.6,
      }}
    >
      <p style={{ margin: 0, whiteSpace: "pre-line" }}>{bio}</p>

      {(hasWebsite || hasSocial) && (
        <div
          style={{
            marginTop: 28,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {hasWebsite && (
            <LinkRow
              label="Website"
              value={creator.website!}
              href={creator.website!}
              muted={muted}
            />
          )}
          {social.twitter && (
            <LinkRow
              label="Twitter / X"
              value={`@${social.twitter}`}
              href={`https://twitter.com/${social.twitter}`}
              muted={muted}
            />
          )}
          {social.instagram && (
            <LinkRow
              label="Instagram"
              value={`@${social.instagram}`}
              href={`https://instagram.com/${social.instagram}`}
              muted={muted}
            />
          )}
          {social.mastodon && (
            <LinkRow
              label="Mastodon"
              value={social.mastodon}
              href={`https://${social.mastodon.split("@").pop()}/@${social.mastodon.split("@")[0]}`}
              muted={muted}
            />
          )}
        </div>
      )}

      {creator.type === "external" && creator.externalHomepage && (
        <p
          style={{
            marginTop: 28,
            fontSize: 13,
            color: muted,
            lineHeight: 1.5,
          }}
        >
          Externer Publisher. Die Originalveröffentlichungen liegen auf{" "}
          <a
            href={creator.externalHomepage}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: PINK, textDecoration: "none" }}
          >
            {creator.externalHomepage.replace(/^https?:\/\//, "")}
          </a>
          .
        </p>
      )}
    </article>
  );
}

function LinkRow({
  label,
  value,
  href,
  muted,
}: {
  label: string;
  value: string;
  href: string;
  muted: string;
}) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
      <span
        style={{
          width: 96,
          fontSize: 12,
          color: muted,
          letterSpacing: 0.2,
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: PINK,
          textDecoration: "none",
          fontSize: 14,
        }}
      >
        {value}
      </a>
    </div>
  );
}
